import NavigationRouter from "@frog/source/utility/NavigationRouter.js"
import UserError from "@frog/source/utility/UserError.js"

import games from "@frog/source/data/games/index.js"
import tests from "@frog/source/tests/testlist.js"
import sizes from "@frog/source/data/sizes.js"
import main from "@frog/source/data/main.js"
import Preload from "@frog/source/data/preload.js"

import App from "@frog/source/models/App.js"
import Dev from "@frog/source/models/admin/Dev.js"
import Audiomix from "@frog/source/juice/Audiomix.js"
import Testlaunch from "@frog/source/tests/utility/Testlaunch.js"

function clearLivePlaytestSavestatesFromGame(game, gameSlug) {
    for(chapterSlug of Object.keys(game.chapters)) {
        localStorage.removeItem("savestate_" + gameSlug + "_" + chapterSlug + "_livePlaytest")
    }
}

////////////////////
// INSTANTIATION //
//////////////////

const Navigation = new NavigationRouter({
    "defaultPath": "/games/" + main.defaultGameSlug,
})

export default Navigation

window.frog = window.frog || {}
window.frog.navigation = Navigation

//////////////////
// GAME ROUTES //
////////////////

Navigation.on("/title", function(request) {
    const gameSlug = main.defaultGameSlug
    const game = games[gameSlug]
    if(game == undefined) throw new UserError("Could not find game:", gameSlug)
    clearLivePlaytestSavestatesFromGame(game, gameSlug)

    Preload.load({"audios": game.audio})
    Audiomix.trigger("Music:Title")
    Navigation.state = {"screen": "TitleScreen", "gameSlug": gameSlug, "isSplashing": false}
})

Navigation.on("/games/:gameSlug", function(request) {
    const gameSlug = request.wildcards.gameSlug
    const game = games[gameSlug]
    if(game == undefined) throw new UserError("Could not find game:", gameSlug)
    clearLivePlaytestSavestatesFromGame(game, gameSlug)

    Preload.load({"audios": game.audio})
    Audiomix.trigger("Music:Title")
    Navigation.state = {"screen": "TitleScreen", "gameSlug": gameSlug}
})

Navigation.on("/games/:gameSlug/reload", function(request) {
    const gameSlug = request.wildcards.gameSlug
    const game = games[gameSlug]
    if(game == undefined) throw new UserError("Could not find game:", gameSlug)

    var savegame
    try {
        savegame = JSON.parse(localStorage.getItem(game.slug + "_savegame"))
    } catch (error) {
        console.warn("No valid savegame in localstorage. Redirecting...")
        Navigation.go("/games/:gameSlug")
        return
    }

    console.info("Reloading game from save...", savegame)
    game.player = savegame.player
    game.randomSeed = savegame.randomSeed

    const chapter = game.chapters[savegame.chapterSlug]
    if(chapter == undefined) throw new UserError("Could not find chapter:", slug)

    Preload.load({"audios": game.audio})
    App.load({"game": {...game, chapter}})
    if(window.location.search.includes("development") == true
    || window.location.search.includes("playtest") == true) {
        if(Dev.mode == "scripts") Dev.readScriptFile()
    }

    Navigation.state = {"screen": "ChapterScreen", "gameSlug": gameSlug, "chapterSlug": savegame.chapterSlug}
    if(window.location.search.includes("playtest") == true) {
        Dev.reloadSavestate("livePlaytest", {"rejectStaleSavestates": true})
    }
})

Navigation.on("/games/:gameSlug/title", function(request) {
    const gameSlug = request.wildcards.gameSlug

    const game = games[gameSlug]
    if(game == undefined) throw new UserError("Could not find game:", gameSlug)

    clearLivePlaytestSavestatesFromGame(game, gameSlug)

    App.game = game

    Preload.load({"audios": game.audio})
    Audiomix.trigger("Music:Title")
    Navigation.state = {"screen": "TitleScreen", "gameSlug": gameSlug, "isSplashing": false}
})

Navigation.on("/games/:gameSlug/chapters/:chapterSlug", function(request) {
    const slug = request.wildcards.chapterSlug
    const gameSlug = request.wildcards.gameSlug

    const game = games[gameSlug]
    if(game == undefined) throw new UserError("Could not find game:", gameSlug)
    const chapter = game.chapters[slug]
    if(chapter == undefined) throw new UserError("Could not find chapter:", slug)

    Preload.load({"audios": game.audio})
    App.load({"game": {...game, chapter}})
    Navigation.state = {"screen": "ChapterScreen", "gameSlug": gameSlug, "chapterSlug": slug}

    if(window.location.search.includes("development") == true
    || window.location.search.includes("playtest") == true) {
        if(Dev.mode == "scripts") Dev.readScriptFile()
    }

    if(window.location.search.includes("playtest") == true) {
        Dev.reloadSavestate("livePlaytest", {"rejectStaleSavestates": true})
    }
})

Navigation.on("/games/:gameSlug/chapters/:chapterSlug/encounters/:encounterSlug", function(request) {
    const slug = request.wildcards.chapterSlug
    const gameSlug = request.wildcards.gameSlug
    const encounterSlug = request.wildcards.encounterSlug

    const game = games[gameSlug]
    if(game == undefined) throw new UserError("Could not find game:", gameSlug)
    const chapter = game.chapters[slug]
    if(chapter == undefined) throw new UserError("Could not find chapter:", slug)

    Preload.load({"audios": game.audio})
    App.load({"game": {...game, chapter}})

    if(encounterSlug == (chapter.startingEncounter || 1)) {
        App.game.chapter.restart()
    } else {
        const isSuccessfulReload = Dev.reloadSavestate("advanceToEncounter" + encounterSlug)
        if(isSuccessfulReload == false) {
            const stage = App.game.chapter.stage
            stage.currentEncounterKey = encounterSlug
            const selection = App.game.chapter.selection
            selection.camera.position = selection.calculateNextCameraPosition(stage)
        }
    }

    Navigation.state = {"screen": "ChapterScreen", "gameSlug": gameSlug, "chapterSlug": slug, "encounterSlug": encounterSlug}
})

Navigation.on("/games/:gameSlug/party", function(request) {
    const gameSlug = request.wildcards.gameSlug
    const game = games[gameSlug]
    if(game == undefined) throw new UserError("Could not find game:", gameSlug)

    Preload.load({"audios": game.audio})
    App.load({"game": {...game}})
    Navigation.state = {"screen": "PartyScreen"}
})

Navigation.on("/games/:gameSlug/overworld", function(request) {
    const gameSlug = request.wildcards.gameSlug
    const game = games[gameSlug]
    if(game == undefined) throw new UserError("Could not find game:", gameSlug)

    Preload.load({"audios": game.audio})
    App.load({"game": {...game}})
    Navigation.state = {"screen": "OverworldScreen"}
})

Navigation.on("/credits", function(request) {
    Audiomix.trigger("Music:Title")
    Navigation.state = {"screen": "CreditsScreen"}
})

Navigation.on("/games/:gameSlug/credits", function(request) {
    const gameSlug = request.wildcards.gameSlug

    const game = games[gameSlug]
    if(game == undefined) throw new UserError("Could not find game:", gameSlug)

    App.game = game

    Preload.load({"audios": game.audio})
    Audiomix.trigger(game.titleMusicKey || "Music:Title")
    Navigation.state = {"screen": "CreditsScreen", "gameSlug": gameSlug}
})

/////////////////
// DEV ROUTES //
///////////////

Navigation.on("/dev", function(request) {
    Navigation.state = {"screen": "DevHellScreen"}
})

Navigation.on("/dev/tests", function(request) {
    Testlaunch.perform(tests)
    Navigation.state = {"screen": "DevTestListScreen"}
})

Navigation.on("/dev/tests/:testslug", function(request) {
    const testslug = request.wildcards.testslug
    const test = tests.find((test) => test.slug == testslug)
    if(test == undefined) {
        throw new UserError("Could not find test " + testslug)
    }
    Testlaunch.perform(test)
    Navigation.state = {"screen": "DevTestScreen", "test": test}
})

Navigation.on("/dev/savetests/:gameslug/:testslug", function(request) {
    const gameslug = request.wildcards.gameslug
    const testslug = request.wildcards.testslug
    const game = games[gameslug]
    const test = game.tests[testslug]
    if(test == undefined) {
        throw new UserError("Could not find test " + testslug)
    }
    //Testlaunch.perform(test)
    if(test.status == undefined) {
        const chapter = game.chapters[test.chapterSlug]
        App.load({"game": {...game, chapter}})
        test.status = Dev.replaySavestate(test) ? "good" : "bad"
    }
    Navigation.state = {"screen": "DevSavetestScreen", "test": test}
})

Navigation.on("/dev/ui", function(request) {
    Navigation.state = {"screen": "DevUiListScreen"}
})

Navigation.on("/dev/ui/:uislug", function(request) {
    Navigation.state = {"screen": "DevUiScreen", "uislug": request.wildcards.uislug}
})

Navigation.on("/dev/imports", function(request) {
    Navigation.state = {"screen": "DevImportsScreen"}
})

Navigation.on("/dev/scripts/import", function(request) {
    Navigation.state = {"screen": "DevScriptImporterScreen"}
})

Navigation.on("/dev/experiment", function(request) {
    Navigation.state = {"screen": "DevExperimentScreen"}
})

Navigation.on("/dev/units", function(request) {
    Navigation.state = {"screen": "DevUnitListScreen"}
})

Navigation.on("/dev/chapters", function(request) {
    Navigation.state = {"screen": "DevChapterListScreen"}
})

Navigation.on("/dev/encounterEditor", function(request) {
    Dev.areDevPanelsOpen = true
    Navigation.state = {"screen": "DevEncounterEditorScreen"}
})

//////////////////
// MISC ROUTES //
////////////////

Navigation.on("/screens/:screenName", function(request) {
    Navigation.state = {"screen": request.wildcards.screenName}
})

Navigation.on("/*", function(request) {
    throw new UserError("Could not find " + request.path)
})

Navigation.onUserError = function(request) {
    Navigation.state = {"screen": "ErrorScreen", "message": request.error.message}
}
Navigation.onError = function(request) {
    Navigation.state = {"screen": "ErrorScreen", "message": "Something went wrong!!"}
}
